import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import UploadFileRoundedIcon from '@mui/icons-material/UploadFileRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import ViewColumnRoundedIcon from '@mui/icons-material/ViewColumnRounded';
import * as XLSX from 'xlsx';
import PartnerService from '../../../services/PartnerService';
import { normalizeImportField } from '../../../helpers/PartnerImportHelper';
import Message from '../../tools/Message';
import ConflitManage, { getConflictOptions, toResolutionPayload } from './ConflitManage';
import { buildPartnerImportTemplate } from './buildPartnerImportTemplate';


const IMPORT_FIELD_SECTIONS = [
  {
    title: 'Partenaire',
    requiredTitle: 'Colonnes obligatoires',
    requiredFields: ['NomPartenaire', 'StatutJuridique', 'NIF', 'STAT'],
    optionalTitle: 'Colonnes optionnelles',
    optionalFields: ['ContactPrincipal', 'EmailPartenaire', 'TéléphonePartenaire'],
  },
  {
    title: 'Évaluation',
    requiredTitle: 'Colonnes obligatoires',
    requiredFields: [],
    optionalTitle: 'Colonnes optionnelles',
    optionalFields: ["Date dernière évaluation", "Résultat de l'évaluation"],
  },
  {
    title: 'Marché',
    requiredTitle: 'Obligatoires si un marché est renseigné',
    requiredFields: ['NumeroMarche', 'ObjetContrat'],
    optionalTitle: 'Colonnes optionnelles',
    optionalFields: [
      'Composante', 'Direction / Antenne', 'ZoneIntervention', 'Financement',
      'MontantMarche', 'DateDebut', 'DateFin', 'Date Rupture de contrat',
      'Cause rupture de Contrat', 'situation',
    ],
  },
  {
    title: 'Personnel',
    requiredTitle: 'Obligatoire si un personnel est renseigné',
    requiredFields: ['NomPersonnel'],
    optionalTitle: 'Colonnes optionnelles',
    optionalFields: [
      'CIN', 'Fonction', 'emailPersonnel', 'adresse', 'telPersonnel',
      'dateNaissance', 'diplome', 'filiere', 'SituationPersonnel',
      'DateDebutContratPersonnel', 'DateFinContratPersonnel', 'CauseRupturePersonnel',
    ],
  },
];
const ACCEPTED_EXTENSIONS = ['.xlsx', '.xls', '.csv'];

const hasAcceptedExtension = (file) => {
  const name = file?.name?.toLowerCase?.() || '';
  return ACCEPTED_EXTENSIONS.some((extension) => name.endsWith(extension));
};

const isImportDataSheet = (headers) => (
  headers.some((header) => normalizeImportField(header) === 'NomPartenaire')
);

const parseFileForLocalPreview = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();

  reader.onerror = () => reject(new Error('Lecture du fichier impossible.'));
  reader.onload = (event) => {
    try {
      const workbook = XLSX.read(event.target?.result, { type: 'array' });
      const sheets = workbook.SheetNames.reduce((result, sheetName) => {
        const sheet = workbook.Sheets[sheetName];
        const matrix = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' });
        const headers = Array.isArray(matrix[0]) ? matrix[0] : [];

        if (!isImportDataSheet(headers)) {
          return result;
        }

        const visibleHeaders = headers.map((header, index) => (
          normalizeImportField(header) || `Colonne ${index + 1}`
        ));
        const rows = matrix.slice(1, 9).map((row) => (
          visibleHeaders.map((_, index) => row[index] ?? '')
        ));

        result.push({
          sheetName,
          headers: visibleHeaders,
          rows,
          totalRows: Math.max(matrix.length - 1, 0),
        });
        return result;
      }, []);

      if (sheets.length === 0) {
        reject(new Error('Aucune feuille de donnees importable trouvee.'));
        return;
      }

      resolve(sheets);
    } catch (error) {
      reject(error instanceof Error ? error : new Error('Erreur lors de la lecture du fichier.'));
    }
  };

  reader.readAsArrayBuffer(file);
});

const buildDefaultConflictDecisions = (conflicts) => (
  conflicts.reduce((acc, conflict) => {
    let savedChoice = conflict.resolution;
    if (savedChoice === 'USE_EXISTING_STAFF') {
      let details = conflict.resolution_data;
      if (typeof details === 'string') {
        try { details = JSON.parse(details); } catch (error) { details = {}; }
      }
      savedChoice = details?.personnel_id ? `USE_EXISTING_STAFF:${details.personnel_id}` : null;
    }
    const options = getConflictOptions(conflict);
    acc[conflict.id] = options.some((option) => option.value === savedChoice)
      ? savedChoice : (options[0]?.value || 'IGNORE_ROW');
    return acc;
  }, {})
);

const INITIAL_IMPORT_MESSAGE = {
  open: false,
  color: 'green',
  messageInfo: '',
};


const ANOMALY_RULES = {
  REQUIRED_FIELDS_MISSING: 'Renseigner les champs partenaire obligatoires.',
  MARKET_REQUIRED_FIELDS_MISSING: 'Renseigner NumeroMarche et ObjetContrat.',
  PARTNER_REFERENCE_STALE: 'Choisir un partenaire dans le dernier modèle PGI–SPM.',
  SPM_UNAVAILABLE_FOR_PARTNER: 'Réessayer lorsque SPM est disponible.',
  PARTNER_IDENTITY_AMBIGUOUS: 'Corriger NIF, STAT ou le partenaire pour obtenir une identité unique.',
  PARTNER_NOT_FOUND: 'Choisir un partenaire PGI ou un titulaire confirmé par SPM.',
  MARKET_NOT_FOUND_IN_PGI: 'Le contrat doit déjà posséder une exécution PGI.',
  MARKET_PARTNER_MISMATCH: 'Le partenaire doit correspondre au titulaire du contrat.',
  STAFF_NAME_REQUIRED: 'Renseigner NomPersonnel dès qu’une donnée de personnel est présente.',
  STAFF_CIN_AMBIGUOUS: 'Le CIN doit identifier une seule personne.',
  STAFF_NAME_AMBIGUOUS: 'Ajouter un CIN unique pour distinguer la personne.',
  STAFF_FILE_CIN_CONTRADICTION: 'Utiliser un seul nom pour le même CIN dans le fichier.',
  STAFF_FILE_NAME_CIN_CONTRADICTION: 'Utiliser un seul CIN pour le même nom dans le fichier.',
  STAFF_CONTRACT_DATES_INVALID: 'La date de fin personnel doit être postérieure ou égale à la date de début.',
  STAFF_TERMINATION_INCONSISTENT: 'Une résiliation personnel exige situation, date de fin et cause cohérentes.',
};

const ImportDialog = (props) => {
  const { openDialog, onClose, onImported, initialPeriod = null } = props;
  const fileInputRef = useRef(null);
  const replaceOnSelectRef = useRef(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [localPreviewSheets, setLocalPreviewSheets] = useState([]);
  const [localPreviewSheet, setLocalPreviewSheet] = useState('');
  const [loadingLocalPreview, setLoadingLocalPreview] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [message, setMessage] = useState(INITIAL_IMPORT_MESSAGE);
  const [titlePeriod, setTitlePeriod] = useState('');
  const [importPeriodId, setImportPeriodId] = useState(null);
  const [openConflictDialog, setOpenConflictDialog] = useState(false);
  const [importConflicts, setImportConflicts] = useState([]);
  const [conflictDecisions, setConflictDecisions] = useState({});
  const [resolvingConflicts, setResolvingConflicts] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [downloadingTemplate, setDownloadingTemplate] = useState(false);
  const [importStatus, setImportStatus] = useState('');
  const [analysisError, setAnalysisError] = useState('');
  const [importAnomalies, setImportAnomalies] = useState([]);
  const [duplicateInfo, setDuplicateInfo] = useState(null);
  const [duplicateRowsCount, setDuplicateRowsCount] = useState(0);
  const { open, color, messageInfo } = message;
  const resetState = useCallback(() => {
    setSelectedFile(null);
    setLocalPreviewSheets([]);
    setLocalPreviewSheet('');
    setLoadingLocalPreview(false);
    setDragActive(false);
    setErrorMessage('');
    setTitlePeriod('');
    setImportPeriodId(null);
    setOpenConflictDialog(false);
    setImportConflicts([]);
    setConflictDecisions({});
    setResolvingConflicts(false);
    setIsSubmitting(false);
    setDownloadingTemplate(false);
    setImportStatus('');
    setAnalysisError('');
    setImportAnomalies([]);
    setDuplicateInfo(null);
    setDuplicateRowsCount(0);
    replaceOnSelectRef.current = false;
    setMessage(INITIAL_IMPORT_MESSAGE);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  useEffect(() => {
    if (openDialog) {
      resetState();
      if (initialPeriod?.part_period_id) {
        setImportPeriodId(initialPeriod.part_period_id);
        setImportStatus(initialPeriod.import_status || 'ACTION_REQUIRED');
        setAnalysisError(initialPeriod.analysis_error || '');
        Promise.all([
          PartnerService.getImportAnomalies(initialPeriod.part_period_id),
          PartnerService.getImportConflicts(initialPeriod.part_period_id),
        ]).then(([anomalyResult, conflictResult]) => {
          const anomalies = Array.isArray(anomalyResult?.data) ? anomalyResult.data : [];
          const conflicts = Array.isArray(conflictResult?.data?.conflicts) ? conflictResult.data.conflicts : [];
          setImportAnomalies(anomalies);
          setImportConflicts(conflicts);
          setConflictDecisions(buildDefaultConflictDecisions(conflicts));
          setOpenConflictDialog(anomalies.length + conflicts.length > 0);
        });
      }
    }
  }, [initialPeriod, openDialog, resetState]);

  const handleDialogClose = useCallback(() => {
    resetState();
    if (typeof onClose === 'function') {
      onClose();
    }
  }, [onClose, resetState]);

  const handleSelectedFile = useCallback(async (file) => {
    if (!file) {
      return;
    }

    if (!hasAcceptedExtension(file)) {
      setSelectedFile(null);
      setLocalPreviewSheets([]);
      setLocalPreviewSheet('');
      setErrorMessage('Formats acceptés : .xlsx .xls .csv');
      return;
    }

    setSelectedFile(file);
    setErrorMessage('');
    setLocalPreviewSheets([]);
    setLocalPreviewSheet('');
    setLoadingLocalPreview(true);

    try {
      const sheets = await parseFileForLocalPreview(file);
      setLocalPreviewSheets(sheets);
      setLocalPreviewSheet(sheets[0]?.sheetName || '');
    } catch (error) {
      setErrorMessage(error?.message || 'Aperçu du fichier impossible.');
    } finally {
      setLoadingLocalPreview(false);
    }
  }, []);

  const openFilePicker = useCallback(() => {
    replaceOnSelectRef.current = false;
    fileInputRef.current?.click();
  }, []);

  const handleDrop = useCallback(
    (event) => {
      event.preventDefault();
      event.stopPropagation();
      setDragActive(false);
      const file = event.dataTransfer?.files?.[0];
      handleSelectedFile(file);
    },
    [handleSelectedFile]
  );

  const activeLocalPreview = localPreviewSheets.find(
    (sheet) => sheet.sheetName === localPreviewSheet,
  ) || localPreviewSheets[0];

  const handleSendFile = useCallback(async (file = selectedFile) => {
    if (!file) {
      return null;
    }
    const formData = new FormData();
    formData.append('file', file);
    formData.append('titlePeriod', titlePeriod || file.name);
    try {
      const result = importPeriodId
        ? await PartnerService.replaceImportFile(importPeriodId, formData)
        : await PartnerService.sendExcelFile(formData);
      if (!result?.status) {
        throw new Error(result?.message || 'Erreur lors de l envoi du fichier.');
      }
      if (result) {
        setMessage({
          open: true,
          color: 'green',
          messageInfo: 'Fichier envoyé avec succès.',
        });
      }
      return result;
    } catch (error) {
      console.error('Erreur lors de l’envoi du fichier :', error);
      setMessage({
        open: true,
        color: 'red',
        messageInfo: 'Erreur lors de l’envoi du fichier.',
      });
      throw error;
    }
  }, [importPeriodId, selectedFile, titlePeriod]);

  const handleImport = useCallback(async (file = selectedFile) => {
    if (!file) {
      setErrorMessage('Veuillez sélectionner un fichier à importer.');
      return;
    }

    setErrorMessage('');

    setIsSubmitting(true);
    setDuplicateInfo(null);
    try {
      {
        const result = await handleSendFile(file);
        setDuplicateInfo(result?.data?.duplicate_file ? { duplicate_file: true } : null);
        setDuplicateRowsCount(Number(result?.data?.analysis?.duplicate_count || 0));
        // Si l'import a été effectué avec succès, on vérifie les anomalies et les conflits
        const conflicts = Array.isArray(result?.data?.conflicts)
          ? result.data.conflicts.filter((conflict) => String(conflict?.conflict_type || '').trim() !== '')
          : [];
        const anomalies = Array.isArray(result?.data?.anomalies) ? result.data.anomalies : [];
        const periodId = result?.data?.period_id || result?.data?.part_period_id || result?.period_id || result?.part_period_id || null;
        const nextStatus = result?.data?.import_status || '';
        setImportPeriodId(periodId || importPeriodId);
        setImportStatus(nextStatus);
        setAnalysisError(nextStatus === 'FAILED' ? (result?.message || 'Analyse du fichier impossible.') : '');
        setImportAnomalies(anomalies);

        setImportConflicts(conflicts);
        setConflictDecisions(buildDefaultConflictDecisions(conflicts));
        if (anomalies.length > 0 || conflicts.length > 0) {
          setOpenConflictDialog(true);
          setMessage({
            open: true,
            color: 'orange',
            messageInfo: result?.message || 'Examinez les lignes avant la synchronisation.',
          });
          if (typeof onImported === 'function') onImported();
          return;
        }
        if (nextStatus === 'FAILED') {
          if (typeof onImported === 'function') onImported();
          return;
        }
      }
      if (typeof onImported === 'function') {
        onImported();
      }
      setTimeout(() => {
      handleDialogClose();
      }, 1000);
    } catch (error) {
      setErrorMessage( error?.message || "Erreur lors de l'import du fichier. Veuillez vérifier le format et les données du fichier.");
      setMessage({
        open: true,
        color: 'red',
        messageInfo: "Erreur lors de l'import du fichier.",
      });
    } finally {
      setIsSubmitting(false);
    }
  }, [handleDialogClose, handleSendFile, importPeriodId, onImported, selectedFile]);

  const handleFileChange = useCallback(async (event) => {
    const file = event.target.files?.[0];
    const replaceNow = replaceOnSelectRef.current;
    replaceOnSelectRef.current = false;
    if (!file) return;
    await handleSelectedFile(file);
    if (replaceNow && hasAcceptedExtension(file)) await handleImport(file);
  }, [handleImport, handleSelectedFile]);

  const handleReplaceFile = useCallback(() => {
    replaceOnSelectRef.current = true;
    if (fileInputRef.current) fileInputRef.current.value = '';
    fileInputRef.current?.click();
  }, []);

  const handleTemplateDownload = useCallback(async () => {
    setDownloadingTemplate(true);
    setErrorMessage('');
    try {
      const references = await PartnerService.getImportTemplateReferences();
      const workbook = await buildPartnerImportTemplate(references);
      const blob = new Blob([workbook], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `template_import_partners_${new Date().toISOString().slice(0, 10).replace(/-/g, '')}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      setErrorMessage(error?.response?.data?.message || error?.message || 'Generation du modele impossible.');
    } finally {
      setDownloadingTemplate(false);
    }
  }, []);

  const handleCancelImportedPeriod = useCallback(async () => {
    if (importPeriodId) {
      await PartnerService.removePeriod(importPeriodId);
    }
    handleDialogClose();
    if (typeof onImported === 'function') onImported();
  }, [handleDialogClose, importPeriodId, onImported]);
  const handleResolveImportConflicts = useCallback(async () => {
    if (!importPeriodId || importConflicts.length + importAnomalies.length === 0) {
      return;
    }

    setResolvingConflicts(true);
    try {
      if (importAnomalies.some((anomaly) => conflictDecisions[anomaly.id] !== 'IGNORE_ROW')) {
        throw new Error('Corrigez le fichier ou ignorez chaque ligne en anomalie avant la soumission.');
      }
      const resolutions = [...importConflicts, ...importAnomalies].filter((issue) => (
        issue.status !== 'BLOCKING_ERROR' || conflictDecisions[issue.id] === 'IGNORE_ROW'
      )).map((conflict) => {
        const selected = conflictDecisions[conflict.id] || getConflictOptions(conflict)[0]?.value || 'IGNORE_ROW';
        return {
          id: conflict.id,
          ...toResolutionPayload(selected),
        };
      });

      if (resolutions.length > 0) {
        const resolveResult = await PartnerService.resolveStaging(importPeriodId, resolutions);
        if (!resolveResult?.status) {
          throw new Error(resolveResult?.message || 'Résolution des lignes impossible.');
        }
      }

      const submitResult = await PartnerService.submitStaging(importPeriodId);
      if (!submitResult?.status) {
        throw new Error(submitResult?.message || 'Soumission du lot impossible.');
      }

      setImportConflicts([]);
      setImportAnomalies([]);
      setConflictDecisions({});
      setImportStatus('READY_FOR_SYNC');
      setMessage({
        open: true,
        color: 'green',
        messageInfo: 'Conflits resolus et lot soumis a la synchronisation.',
      });
      if (typeof onImported === 'function') {
        onImported();
      }
      setTimeout(() => {
        handleDialogClose();
      }, 1000);
    } catch (error) {
      setMessage({
        open: true,
        color: 'red',
        messageInfo: error?.message || 'Erreur lors de la resolution des conflits.',
      });
    } finally {
      setResolvingConflicts(false);
    }
  }, [conflictDecisions, handleDialogClose, importAnomalies, importConflicts, importPeriodId, onImported]);

  return (
    <Dialog
      open={openDialog}
      onClose={(event, reason) => {
        if (reason !== 'backdropClick' && !loadingLocalPreview && !resolvingConflicts && !isSubmitting) handleDialogClose();
      }}
      fullWidth
      maxWidth="lg"
      PaperProps={{
        sx: {
          borderRadius: 2,
          overflow: 'hidden',
          border: '1px solid #cbd8e6',
          boxShadow: '0 20px 48px rgba(23, 61, 97, 0.18)',
        },
      }}
    >
      <DialogTitle
        sx={{
          px: 3.5,
          py: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          color: '#ffffff',
          backgroundColor: '#173d61',
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box
            sx={{
              width: 34,
              height: 34,
              borderRadius: 1.5,
              display: 'grid',
              placeItems: 'center',
              color: '#173d61',
              backgroundColor: '#ffffff',
            }}
          >
            <UploadFileRoundedIcon fontSize="medium" />
          </Box>
          <Typography variant="h5" sx={{ fontWeight: 700, fontSize: 24 }}>
            Mise à jour des données partenaire
          </Typography>
        </Stack>
        <IconButton onClick={handleDialogClose} size="small" sx={{ color: '#ffffff' }}>
          <CloseRoundedIcon />
        </IconButton>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ px: 3.5, py: 2.5, backgroundColor: '#f6f9fc' }}>
        <Message open={open} color={color} messageInfo={messageInfo} onClose={() => setMessage({ ...message, open: false })} />
        <Stack spacing={2.5}>

          <Alert
            severity="info"
            icon={<InfoOutlinedIcon fontSize="inherit" />}
            sx={{
              alignItems: 'center',
              borderRadius: 2,
              border: '1px solid #d5e5ff',
              backgroundColor: '#eef4ff',
              color: '#24324b',
              '& .MuiAlert-message': { width: '100%' },
            }}
          >
            <Typography component="span" sx={{ fontWeight: 700 }}>
              Importez un fichier Excel (.xlsx , .xls , .csv).
            </Typography>{' '}
            Cet import met à jour partenaires, contrats existants, évaluations et personnels.
          </Alert>
            <Typography sx={{ display: 'flex', alignItems: 'center', color: '#24324b', fontWeight: 600 }}>
              Télécharger le modèle d’import{' '}
              <Button
                variant="text"
                startIcon={<ViewColumnRoundedIcon />}
                sx={{ textTransform: 'none', fontSize: 15, color: '#1d4ed8' }}
                onClick={handleTemplateDownload}
                disabled={downloadingTemplate}
              >
               {downloadingTemplate ? 'Génération...' : '(Modele_import_partners.xlsx)'}
              </Button>
            </Typography>
             <Grid item xs={12} md={12} lg={12}>
                          <TextField
                            fullWidth
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: 3,
                              },
                            }}
                            size="small"
                            variant="outlined"
                            value={titlePeriod}
                            onChange={(event) => setTitlePeriod(event.target.value)}
                            placeholder="Intitulé période..."
                          />
                        </Grid>
          <Box
            onClick={openFilePicker}
            role="button"
            tabIndex={0}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                openFilePicker();
              }
            }}
            onDragOver={(event) => {
              event.preventDefault();
              setDragActive(true);
            }}
            onDragLeave={(event) => {
              event.preventDefault();
              setDragActive(false);
            }}
            onDrop={handleDrop}
            sx={{
              px: 3,
              py: 4,
              borderRadius: 3,
              border: dragActive ? '2px solid #3b82f6' : '2px dashed #7fb3ff',
              background: dragActive
                ? 'linear-gradient(180deg, #f4f9ff 0%, #edf5ff 100%)'
                : 'linear-gradient(180deg, #f8fbff 0%, #f2f7ff 100%)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center',
              cursor: 'pointer',
              transition: 'border-color 160ms ease, background 160ms ease, transform 160ms ease',
              '&:hover': {
                transform: 'translateY(-1px)',
              },
            }}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept={ACCEPTED_EXTENSIONS.join(',')}
              hidden
              onChange={handleFileChange}
            />

            <Box
              sx={{
                width: 78,
                height: 78,
                mb: 2,
                borderRadius: 2.5,
                display: 'grid',
                placeItems: 'center',
                background: 'linear-gradient(180deg, #4f8ef7 0%, #2563eb 100%)',
                color: '#ffffff',
                boxShadow: '0 12px 24px rgba(37, 99, 235, 0.22)',
              }}
            >
              <UploadFileRoundedIcon sx={{ fontSize: 42 }} />
            </Box>

            <Typography sx={{ fontSize: 18, fontWeight: 700, color: '#22518c' }}>
              Glisser-déposer votre fichier ici
            </Typography>
            <Typography sx={{ mt: 0.75, fontSize: 15, color: '#335b91' }}>
              ou cliquer pour sélectionner un fichier
            </Typography>

            {selectedFile && (
              <Typography sx={{ mt: 1.5, fontSize: 13, fontWeight: 600, color: '#1d4ed8' }}>
                Fichier sélectionné : {selectedFile.name}
              </Typography>
            )}

            {errorMessage && (
              <Typography sx={{ mt: 1.25, fontSize: 13, fontWeight: 600, color: '#c2410c' }}>
                {errorMessage}
              </Typography>
            )}
          </Box>

          {selectedFile && (
            <Box
              sx={{
                border: '1px solid #dbe4ef',
                borderRadius: 2,
                backgroundColor: '#ffffff',
                overflow: 'hidden',
              }}
            >
              <Box sx={{ px: 2, py: 1.5, backgroundColor: '#f8fbff', borderBottom: '1px solid #dbe4ef' }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={0.75} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }}>
                  <Box>
                    <Typography sx={{ fontWeight: 800, color: '#1f2a44' }}>
                      Aperçu des données
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {loadingLocalPreview
                        ? 'Lecture du fichier...'
                        : `${activeLocalPreview?.totalRows || 0} ligne(s) détectée(s)`}
                    </Typography>
                  </Box>
                  {localPreviewSheets.length > 1 && (
                    <Stack direction="row" spacing={0.75} sx={{ flexWrap: 'wrap' }}>
                      {localPreviewSheets.map((sheet) => (
                        <Button
                          key={sheet.sheetName}
                          size="small"
                          variant={sheet.sheetName === activeLocalPreview?.sheetName ? 'contained' : 'outlined'}
                          onClick={() => setLocalPreviewSheet(sheet.sheetName)}
                          sx={{ textTransform: 'none', minWidth: 0 }}
                        >
                          {sheet.sheetName}
                        </Button>
                      ))}
                    </Stack>
                  )}
                </Stack>
              </Box>

              {loadingLocalPreview ? (
                <Box sx={{ p: 3, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">Préparation de l’aperçu...</Typography>
                </Box>
              ) : activeLocalPreview ? (
                <TableContainer sx={{ maxHeight: 310 }}>
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 800, backgroundColor: '#f8fafc', whiteSpace: 'nowrap' }}>#</TableCell>
                        {activeLocalPreview.headers.map((header) => (
                          <TableCell key={header} sx={{ fontWeight: 800, backgroundColor: '#f8fafc', whiteSpace: 'nowrap' }}>
                            {header}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {activeLocalPreview.rows.map((row, rowIndex) => (
                        <TableRow key={`${activeLocalPreview.sheetName}-${rowIndex}`} hover>
                          <TableCell sx={{ color: 'text.secondary' }}>{rowIndex + 1}</TableCell>
                          {row.map((value, columnIndex) => (
                            <TableCell key={`${rowIndex}-${columnIndex}`} sx={{ whiteSpace: 'nowrap', maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {String(value).trim() || '-'}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Alert severity="warning" sx={{ m: 1.5 }}>Aucune ligne exploitable dans ce fichier.</Alert>
              )}
            </Box>
          )}

          <Stack spacing={1.5}>
            <Typography sx={{ fontWeight: 700, fontSize: 18, color: '#24324b' }}>
              Colonnes attendues
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' },
                gap: 2,
                alignItems: 'stretch',
              }}
            >
              {IMPORT_FIELD_SECTIONS.map((section) => (
                <Box
                  key={section.title}
                  sx={{
                    border: '1px solid #cbd8e6',
                    borderRadius: 2,
                    p: 2,
                    backgroundColor: '#ffffff',
                  }}
                >
                  <Typography sx={{ fontWeight: 800, fontSize: 17, color: '#173d61', mb: 1.5 }}>
                    {section.title}
                  </Typography>

                  {section.requiredFields.length > 0 && (
                    <>
                      <Typography sx={{ display: 'flex', alignItems: 'center', gap: 0.75, fontWeight: 700, fontSize: 14, color: '#b45309', mb: 0.75 }}>
                        <WarningAmberRoundedIcon sx={{ fontSize: 18, color: '#fb923c' }} />
                        {section.requiredTitle}
                      </Typography>
                      <Box component="ul" sx={{ m: 0, mb: 1.5, pl: 2.5, color: '#2f3b52' }}>
                        {section.requiredFields.map((field) => (
                          <Box component="li" key={`${section.title}-required-${field}`} sx={{ mb: 0.65, fontSize: 14 }}>
                            {field}
                          </Box>
                        ))}
                      </Box>
                    </>
                  )}

                  <Typography sx={{ display: 'flex', alignItems: 'center', gap: 0.75, fontWeight: 700, fontSize: 14, color: '#1d4ed8', mb: 0.75 }}>
                    <ViewColumnRoundedIcon sx={{ fontSize: 18, color: '#22c55e' }} />
                    {section.optionalTitle}
                  </Typography>
                  <Box component="ul" sx={{ m: 0, pl: 2.5, color: '#2f3b52' }}>
                    {section.optionalFields.map((field) => (
                      <Box component="li" key={`${section.title}-optional-${field}`} sx={{ mb: 0.65, fontSize: 14 }}>
                        {field}
                      </Box>
                    ))}
                  </Box>
                </Box>
              ))}
            </Box>
          </Stack>

          {duplicateInfo && (
            <Alert
              severity="info"
              sx={{ border: '1px solid #d5ad3f', borderRadius: 2 }}
            >
              Ce fichier a déjà été importé. Une nouvelle période a été créée sans bloquer l’analyse.
            </Alert>
          )}

          {duplicateRowsCount > 0 && (
            <Alert severity="info" sx={{ border: '1px solid #cbd8e6', borderRadius: 2 }}>
              {duplicateRowsCount} ligne(s) identique(s) détectée(s) : seule la première occurrence de chaque ligne sera synchronisée.
            </Alert>
          )}

          {(importStatus === 'ACTION_REQUIRED' || importStatus === 'READY_FOR_SYNC')
            && (importAnomalies.length > 0 || importConflicts.length > 0) && (
              <Alert severity="warning" sx={{ border: '1px solid #f5ddb5', borderRadius: 2 }}>
                {importAnomalies.length} anomalie(s) et {importConflicts.length} conflit(s) détectés.
                Gérez-les dans le dialogue de traitement des lignes.
                <Button size="small" onClick={() => setOpenConflictDialog(true)}>Ouvrir</Button>
              </Alert>
            )}

          {importStatus === 'FAILED' && analysisError && (
            <Box>
              <Alert severity="error" sx={{ border: '1px solid #efb5b5', borderRadius: 2 }}>
                <strong>Analyse échouée :</strong> {analysisError} Sélectionnez un fichier corrigé puis remplacez l’import.
              </Alert>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 1 }}>
                <Button variant="outlined" onClick={handleCancelImportedPeriod} disabled={isSubmitting}>Annuler l’import</Button>
                <Button variant="outlined" onClick={handleReplaceFile} disabled={isSubmitting}>Remplacer le fichier</Button>
              </Stack>
            </Box>
          )}

          <ConflitManage
            open={openConflictDialog && importConflicts.length + importAnomalies.length > 0}
            onClose = {() => setOpenConflictDialog(false)}
            importConflicts={importConflicts}
            importAnomalies={importAnomalies}
            anomalyRules={ANOMALY_RULES}
            conflictDecisions={conflictDecisions}
            setConflictDecisions={setConflictDecisions}
            handleResolveImportConflicts={handleResolveImportConflicts}
            resolvingConflicts={resolvingConflicts}
            onCancelImport={handleCancelImportedPeriod}
            onReplaceFile={handleReplaceFile}
            isSubmitting={isSubmitting}
          />

          <Alert
            icon={<WarningAmberRoundedIcon fontSize="inherit" />}
            severity="warning"
            sx={{
              alignItems: 'center',
              borderRadius: 2,
              border: '1px solid #f5ddb5',
              backgroundColor: '#fff8eb',
              color: '#3f3a2f',
            }}
          >
            Vérifiez que les noms des colonnes correspondent exactement et que les données sont bien formatées.
          </Alert>
        </Stack>
      </DialogContent>

      <Divider />

      <DialogActions sx={{ px: 3.5, py: 2.25, gap: 1.5 }}>
        <Button
          onClick={handleDialogClose}
          variant="outlined"
          sx={{
            minWidth: 132,
            borderRadius: 1.5,
            px: 2.5,
            py: 1.1,
            textTransform: 'none',
            fontWeight: 700,
            fontSize: 16,
            borderColor: '#d1d9e6',
            color: '#2f3b52',
          }}
        >
          Annuler
        </Button>
        <Button
          onClick={() => handleImport()}
          variant="contained"
          disabled={!selectedFile || importConflicts.length > 0 || resolvingConflicts || isSubmitting}
          startIcon={isSubmitting ? <CircularProgress size={18} color="inherit" /> : <UploadFileRoundedIcon />}
          sx={{
            minWidth: 170,
            borderRadius: 1.5,
            px: 2.75,
            py: 1.1,
            textTransform: 'none',
            fontWeight: 700,
            fontSize: 16,
              backgroundColor: '#173d61',
              boxShadow: '0 10px 20px rgba(23, 61, 97, 0.2)',
              '&:hover': {
              backgroundColor: '#102f4c',
              boxShadow: '0 12px 22px rgba(23, 61, 97, 0.26)',
            },
            '&.Mui-disabled': {
              color: '#ffffff',
              opacity: 0.62,
            },
          }}
        >
          {isSubmitting ? 'Analyse en cours...' : importPeriodId ? 'Remplacer le fichier' : 'Importer'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ImportDialog;
